import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { X, ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import VideoModal from "./VideoModal";
import ScrollCue from "./ScrollCue";
import SEO from "./SEO";

const SECTIONS = [
  { id: "films",  label: "Films" },
  { id: "print",  label: "Print" },
  { id: "social", label: "Viral & Social" },
];

/**
 * Work page — single scroll page with all three sections stacked. The hero
 * pills act as anchor links that smoothly scroll to the corresponding section
 * and stay visually active as the reader scrolls. Modals for film / print /
 * viral playback stay intact.
 */
const Work = () => {
  const { WORKS, PRINTS, VIRAL_SOCIAL, WORK_HEADER_VIDEO, BRAND } = useContent();

  // Film modal
  const [activeIndex, setActiveIndex] = useState(null);
  // Print carousel modal
  const [printBrand, setPrintBrand] = useState(null); // {brand, index, dir}
  // Viral video modal
  const [socialPlaying, setSocialPlaying] = useState(null);
  // Which section is currently in view (drives pill highlight)
  const [activeSection, setActiveSection] = useState("films");

  const videoRef = useRef(null);
  const sectionRefs = {
    films:  useRef(null),
    print:  useRef(null),
    social: useRef(null),
  };

  // Smooth-scroll helper for the hero pills
  const scrollTo = useCallback((id) => {
    const el = sectionRefs[id]?.current;
    if (!el) return;
    // Offset for the fixed header
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Highlight the pill matching the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-section");
            if (id) setActiveSection(id);
          }
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
    );
    Object.entries(sectionRefs).forEach(([id, ref]) => {
      if (ref.current) {
        ref.current.setAttribute("data-section", id);
        observer.observe(ref.current);
      }
    });
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hero video autoplay
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  // Scroll to section when arriving via hash (e.g. /work#print)
  const location = useLocation();
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) return;
    // Wait a beat so the section refs exist after render
    const t = setTimeout(() => {
      const el = sectionRefs[hash]?.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 200);
    return () => clearTimeout(t);
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Print carousel keyboard nav
  const printGo = useCallback(
    (dir) => {
      if (!printBrand) return;
      const total = printBrand.brand.images.length;
      setPrintBrand({
        ...printBrand,
        index: (printBrand.index + dir + total) % total,
        dir,
      });
    },
    [printBrand],
  );

  useEffect(() => {
    if (!printBrand) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPrintBrand(null);
      if (e.key === "ArrowLeft") printGo(-1);
      if (e.key === "ArrowRight") printGo(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [printBrand, printGo]);

  return (
    <div className="bg-canvas text-ink min-h-screen">
      <SEO
        title="The Work · Films, Print & Viral Campaigns | SAMURAI"
        description="Explore SAMURAI's portfolio of brand films, print campaigns and viral social work for Love Beauty & Planet, Godrej, Comfort, Horlicks, Bru and more."
        path="/work"
      />
      {/* Hero */}
      <section className="dark-block relative min-h-[86vh] md:min-h-[92vh] flex items-end overflow-hidden pt-24 bg-canvas">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            src={WORK_HEADER_VIDEO}
            className="hero-video absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="absolute inset-0 hero-scrim" />
          <div className="absolute inset-0 pointer-events-none noise-overlay" />
        </div>

        <div className="relative mx-auto px-6 md:px-10 w-full pb-16 md:pb-24">
          <h1 className="font-heading font-black text-[40px] md:text-[72px] leading-[0.92] tracking-[-0.03em] uppercase fade-up">
            The Work
          </h1>

          <div
            className="mt-8 flex flex-wrap gap-3 fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="work-pill-nav"
          >
            {SECTIONS.map((t) => (
              <button
                key={t.id}
                onClick={() => scrollTo(t.id)}
                data-testid={`work-pill-${t.id}`}
                className={`px-5 py-2 border rounded-full text-[11px] uppercase tracking-[0.25em] transition-all duration-500 backdrop-blur ${
                  activeSection === t.id
                    ? "bg-ink text-canvas border-ink"
                    : "border-ink/40 text-ink bg-black/25 hover:bg-black/45 hover:border-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <ScrollCue right={BRAND.locations} />
      </section>

      {/* FILMS */}
      <section
        ref={sectionRefs.films}
        id="work-films"
        className="scroll-mt-24 py-20 md:py-24 border-t border-ink/10"
      >
        <SectionHeader title="Films" />
        <div className="mx-auto px-6 md:px-10 mt-10">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
            data-testid="films-grid"
          >
            {WORKS.map((w) => (
              <button
                key={w.id}
                onClick={() => {
                  if (!w.youtubeId) return;
                  setActiveIndex(WORKS.findIndex((it) => it.id === w.id));
                }}
                className="group text-left focus:outline-none"
                data-testid={`film-card-${w.id}`}
              >
                <ThumbTile title={w.title}>
                  <img
                    src={w.image}
                    onError={(e) => (e.currentTarget.src = w.imageFallback)}
                    alt={w.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                  />
                </ThumbTile>
              </button>
            ))}
          </div>

          <div className="mt-14 md:mt-20 flex justify-center">
            <button
              type="button"
              onClick={() => scrollTo("print")}
              data-testid="films-scroll-cue"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ink/60 hover:text-ink transition-colors"
            >
              Scroll
              <ChevronDown className="w-4 h-4 animate-scroll-cue" />
            </button>
          </div>
        </div>
      </section>

      {/* PRINT */}
      <section
        ref={sectionRefs.print}
        id="work-print"
        className="scroll-mt-24 py-20 md:py-24 border-t border-ink/10"
      >
        <SectionHeader title="Print" />
        <div className="mx-auto px-6 md:px-10 mt-10">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
            data-testid="print-grid"
          >
            {PRINTS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPrintBrand({ brand: p, index: 0, dir: 1 })}
                data-testid={`print-card-${p.id}`}
                className="group text-left focus:outline-none"
              >
                <ThumbTile title={p.title}>
                  <img
                    src={p.hero}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                  />
                </ThumbTile>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* VIRAL & SOCIAL */}
      <section
        ref={sectionRefs.social}
        id="work-social"
        className="scroll-mt-24 py-20 md:py-24 border-t border-ink/10"
      >
        <SectionHeader title="Viral & Social" />
        <div className="mx-auto px-6 md:px-10 mt-10">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
            data-testid="viral-grid"
          >
            {VIRAL_SOCIAL.map((v) => (
              <ViralCard key={v.id} item={v} onOpen={() => setSocialPlaying(v)} />
            ))}
          </div>
        </div>
      </section>

      {/* Film modal */}
      <VideoModal
        list={WORKS}
        index={activeIndex}
        open={activeIndex !== null}
        onIndexChange={setActiveIndex}
        onClose={() => setActiveIndex(null)}
      />

      {/* Viral video modal — YouTube embed via shared modal */}
      <VideoModal
        videoId={socialPlaying?.youtubeId}
        meta={socialPlaying}
        open={!!socialPlaying}
        onClose={() => setSocialPlaying(null)}
      />

      {/* Print carousel modal */}
      {printBrand && (
        <div
          role="dialog"
          data-testid="print-carousel"
          className="fixed inset-0 z-[80] bg-canvas/95 backdrop-blur-md flex flex-col items-center justify-center px-4 md:px-10 py-8"
          onClick={() => setPrintBrand(null)}
        >
          <button
            type="button"
            aria-label="Close preview"
            data-testid="print-carousel-close"
            onClick={() => setPrintBrand(null)}
            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-ink/40 hover:bg-ink hover:text-canvas text-ink flex items-center justify-center transition-colors z-[81]"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-full max-w-[1200px] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-black text-2xl md:text-3xl tracking-[-0.02em] uppercase text-ink mb-8">
              {printBrand.brand.title}
            </h3>

            <div className="relative w-full flex items-center justify-center">
              {printBrand.brand.images.length > 1 &&
                (() => {
                  const total = printBrand.brand.images.length;
                  const prevIdx = (printBrand.index - 1 + total) % total;
                  const nextIdx = (printBrand.index + 1) % total;
                  const slideCls = printBrand.dir >= 0 ? "slide-in-right" : "slide-in-left";
                  return (
                    <>
                      <button
                        type="button"
                        aria-label="Previous print"
                        onClick={() => printGo(-1)}
                        className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 w-[22%] rounded-2xl overflow-hidden opacity-40 hover:opacity-70 transition-opacity duration-500 z-0 bg-black"
                        style={{ aspectRatio: printBrand.brand.aspect }}
                      >
                        <img
                          key={`prev-${prevIdx}`}
                          src={printBrand.brand.images[prevIdx]}
                          alt=""
                          className={`w-full h-full object-contain ${slideCls}`}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        aria-label="Next print"
                        onClick={() => printGo(1)}
                        className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 w-[22%] rounded-2xl overflow-hidden opacity-40 hover:opacity-70 transition-opacity duration-500 z-0 bg-black"
                        style={{ aspectRatio: printBrand.brand.aspect }}
                      >
                        <img
                          key={`next-${nextIdx}`}
                          src={printBrand.brand.images[nextIdx]}
                          alt=""
                          className={`w-full h-full object-contain ${slideCls}`}
                          aria-hidden="true"
                        />
                      </button>
                    </>
                  );
                })()}

              <div
                className="relative z-10 w-full md:w-[62%] bg-black rounded-2xl overflow-hidden shadow-2xl shadow-black/60"
                style={{ aspectRatio: printBrand.brand.aspect }}
              >
                <img
                  key={printBrand.index}
                  src={printBrand.brand.images[printBrand.index]}
                  alt={`${printBrand.brand.title} print ${printBrand.index + 1}`}
                  className={`w-full h-full object-contain ${printBrand.dir >= 0 ? "slide-in-right" : "slide-in-left"}`}
                  data-testid="print-carousel-image"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center gap-5">
              <button
                type="button"
                aria-label="Previous print"
                data-testid="print-carousel-prev"
                onClick={() => printGo(-1)}
                className="w-10 h-10 rounded-full border border-ink/40 text-ink hover:bg-ink hover:text-canvas flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-2">
                {printBrand.brand.images.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to print ${i + 1}`}
                    onClick={() =>
                      setPrintBrand({
                        ...printBrand,
                        index: i,
                        dir: i >= printBrand.index ? 1 : -1,
                      })
                    }
                    className={`transition-all duration-300 rounded-full ${
                      i === printBrand.index
                        ? "w-2.5 h-2.5 bg-ink"
                        : "w-2 h-2 bg-ink/30 hover:bg-ink/60"
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                aria-label="Next print"
                data-testid="print-carousel-next"
                onClick={() => printGo(1)}
                className="w-10 h-10 rounded-full border border-ink/40 text-ink hover:bg-ink hover:text-canvas flex items-center justify-center transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <p className="mt-3 text-[10px] tracking-[0.32em] uppercase text-ink/55" data-testid="print-carousel-counter">
              {String(printBrand.index + 1).padStart(2, "0")} /{" "}
              {String(printBrand.brand.images.length).padStart(2, "0")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title }) => (
  <div className="mx-auto px-6 md:px-10">
    <h2 className="font-heading font-black text-[24px] md:text-[40px] leading-[0.95] tracking-[-0.02em] uppercase">
      {title}
    </h2>
  </div>
);

/**
 * ThumbTile — universal thumbnail card with:
 *  · rounded aspect-video canvas
 *  · subtle grey tint on hover (works in both themes)
 *  · centered film title revealed on hover (Geist, sleek)
 * Children are the media element (img or video) with its own scale animation.
 */
const ThumbTile = ({ title, children }) => (
  <div className="relative overflow-hidden rounded-2xl aspect-video bg-neutral-900 dark-block">
    {children}
    <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/75 transition-colors duration-500" />
    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
      <span
        className="text-ink text-[15px] md:text-[17px] font-normal uppercase tracking-[0.18em] text-center px-6"
        style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}
      >
        {title}
      </span>
    </div>
  </div>
);

/**
 * ViralCard — starts playback only when the pointer enters the tile, pauses +
 * rewinds when it leaves. Uses ThumbTile for the tint + title reveal.
 */
const ViralCard = ({ item, onOpen }) => {
  const ref = useRef(null);
  const handleEnter = () => {
    const v = ref.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = ref.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  return (
    <button
      onClick={onOpen}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      data-testid={`viral-card-${item.id}`}
      className="group text-left focus:outline-none"
    >
      <ThumbTile title={item.title}>
        <video
          ref={ref}
          src={item.video}
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
        />
      </ThumbTile>
    </button>
  );
};

export default Work;
